import api from '../api/config';
import localStorageService from './LocalStorageService';

class AuthService {
  // Login user
  async login(email, password) {
    try {
      const response = await api.post('/api/auth/login/', {
        email,
        password
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: this.extractErrorMessage(error)
      };
    }
  }

  // Register user
  async register(email, password, confirmPassword, firstName, lastName, department, institute_id, mobileNumber = null, recaptchaToken = null) {
    try {
      const payload = {
        username: email,
        email,
        password,
        password2: confirmPassword,
        first_name: firstName,
        last_name: lastName,
        department: department || null,
        institute_id: institute_id,
        mobile_number: mobileNumber || null
      };

      // Include reCAPTCHA token if provided
      if (recaptchaToken) {
        payload.recaptcha_token = recaptchaToken;
        console.log('✓ Including reCAPTCHA token in registration request');
      } else {
        console.warn('⚠ No reCAPTCHA token provided for registration');
      }

      const response = await api.post('/api/auth/register/', payload);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: this.extractErrorMessage(error)
      };
    }
  }

  // Refresh token
  async refreshToken(refreshToken) {
    try {
      const response = await api.post('/api/auth/refresh/', {
        refresh: refreshToken
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: this.extractErrorMessage(error)
      };
    }
  }

  // Verify email with token
  async verifyEmail(token) {
    try {
      const response = await api.post('/api/auth/verify-email/', { token });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: this.extractErrorMessage(error),
        data: error.response?.data
      };
    }
  }

  // Resend verification email
  async resendVerificationEmail(token) {
    try {
      const response = await api.post('/api/auth/resend-verification/', { token });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: this.extractErrorMessage(error),
        data: error.response?.data
      };
    }
  }

  // Extract error message from API response
  extractErrorMessage(error) {
    if (error.response?.data) {
      const data = error.response.data;

      // Handle different error formats
      if (data.detail) return data.detail;
      if (data.message) return data.message;
      if (data.error) return data.error;

      // Handle field-specific errors
      if (data.email && Array.isArray(data.email)) {
        return `Email: ${data.email[0]}`;
      }
      if (data.password && Array.isArray(data.password)) {
        return `Password: ${data.password[0]}`;
      }
      if (data.non_field_errors && Array.isArray(data.non_field_errors)) {
        return data.non_field_errors[0];
      }

      // Handle object with multiple field errors
      const firstError = Object.values(data).find(value =>
        Array.isArray(value) && value.length > 0
      );
      if (firstError) return firstError[0];
    }

    // Default error messages based on status code
    if (error.response?.status === 401) {
      return "Invalid credentials. Please check your email and password.";
    }
    if (error.response?.status === 400) {
      return "Invalid request. Please check your input.";
    }
    if (error.response?.status >= 500) {
      return "Server error. Please try again later.";
    }

    return "An unexpected error occurred. Please try again.";
  }

  // Store authentication data
  storeAuthData(data) {
    console.log('Storing auth data:', JSON.stringify(data, null, 2));
    console.log('Data structure:', Object.keys(data));

    // Store tokens
    if (data.access) {
      localStorageService.setAuthToken(data.access);
    }
    if (data.refresh) {
      localStorageService.setRefreshToken(data.refresh);
    }

    // Store roles if available - check multiple possible locations
    if (data.roles && Array.isArray(data.roles)) {
      localStorageService.setAllRoles(data.roles);
      console.log('Stored roles from data.roles:', data.roles);
    } else if (data.user?.roles && Array.isArray(data.user.roles)) {
      localStorageService.setAllRoles(data.user.roles);
      console.log('Stored roles from data.user.roles:', data.user.roles);
    }

    // Store department if it's in the response
    if (data.department) {
      localStorageService.setDepartment(data.department);
      console.log('Stored department from login response:', data.department);
    }

    // Store instructor_id if it's in the response
    if (data.instructor_id) {
      localStorageService.setInstructorId(data.instructor_id);
      console.log('Stored instructor_id from login response:', data.instructor_id);
    }

    // Handle user data storage
    if (data.user) {
      localStorageService.setUser(data.user);
    } else if (data.access) {
      // Try to extract user data from token or create from response data
      let userId = data.user_id || 0;
      try {
        const payload = JSON.parse(atob(data.access.split('.')[1]));
        console.log('Token payload:', payload);
        userId = payload.user_id || data.user_id || 0;
      } catch (e) {
        console.error('Error parsing token:', e);
      }

      // Create user data from response or token
      const userData = {
        id: userId,
        email: data.email || 'unknown@example.com',
        name: data.username || 'Unknown User',
        department: data.department || '',
        is_active: true,
        is_staff: false
      };

      localStorageService.setUser(userData);
    }


  }

  // Clear authentication data
  clearAuthData() {
    localStorageService.clearAuthData();
  }

  // Check if user is authenticated
  isAuthenticated() {
    return localStorageService.isAuthenticated();
  }

  // Get current user data
  getCurrentUser() {
    return localStorageService.getUser();
  }

  // Get auth token
  getAuthToken() {
    return localStorageService.getAuthToken();
  }

  // Get refresh token
  getRefreshToken() {
    return localStorageService.getRefreshToken();
  }

  // Get default role (lowest privilege level for safety)
  // Priority: instructor (level 1) < institute_admin (level 2) < super_admin (level 3)
  getDefaultRole(rolesArray) {
    if (!Array.isArray(rolesArray) || rolesArray.length === 0) {
      return null;
    }

    const rolePriority = {
      'instructor': 1,
      'institute_admin': 2,
      'super_admin': 3
    };

    // Sort by priority (ascending) and return the first (lowest privilege)
    return rolesArray.sort((a, b) => 
      (rolePriority[a] || 999) - (rolePriority[b] || 999)
    )[0];
  }

  // Store selected role to localStorage
  storeSelectedRole(role) {
    localStorageService.setSelectedRole(role);
  }

  // Get stored selected role from localStorage
  getStoredSelectedRole() {
    return localStorageService.getSelectedRole();
  }

  // Get all roles for current user
  getAllRoles() {
    return localStorageService.getAllRoles();
  }

  // Get redirect path based on role (centralized route logic)
  getRoleRedirectPath(role) {
    switch (role) {
      case 'super_admin':
        return '/users-dashboard';
      case 'institute_admin':
        return '/admin-dashboard';
      case 'instructor':
      default:
        return '/courses';
    }
  }
}

export default new AuthService();
